import {
	ContentUnifiedQuery,
	ContentBlogUnifiedQuery,
	ContentUnifiedWithSpaceKeyQuery,
} from '@confluence/content-unified-query';
import { isUnauthorizedError } from '@confluence/query-preloader-tools/entry-points/isUnauthorizedError';
import { query, writeQuery } from '@confluence/query-preloader-tools/entry-points/graphql';
import type {
	ContentUnifiedQueryType,
	ContentUnifiedQueryVariablesType,
	ContentBlogUnifiedQueryVariablesType,
	ContentUnifiedWithSpaceKeyQueryType,
	ContentUnifiedWithSpaceKeyQueryVariablesType,
} from '@confluence/content-unified-query';
import { ssrPermissionsErrorHandler } from '@confluence/ssr-utilities/entry-points/SSRPermissionsErrorHandler';

type PreloadContentParameters = {
	contentId: string;
	isBlog: boolean;
	spaceKey: string;
	shouldPreloadUsingSpaceKey?: boolean;
	versionOverride?: number | null;
};

type PreloadContentResult = {
	result?: any;
	hasErrors: boolean;
};

type ChooseContentUnifiedQueryVariablesType =
	| ContentUnifiedQueryVariablesType
	| ContentBlogUnifiedQueryVariablesType;

function processContentPreloadPermissionError(result: any) {
	result?.errors?.forEach((error: any) => {
		// The "Cannot find space" condition is handled by redirecting to the correct space
		// even if error is not propagated to ContentPrerequisites where it is normally handled.
		if (isUnauthorizedError(error)) {
			ssrPermissionsErrorHandler.markSSRPreloadPermissionError('view', error);
		}
	});
}

export const transformContentData = (data: any) => ({
	content: data?.spaceHomepage
		? {
				nodes: [data?.spaceHomepage],
				// @ts-ignore
				__typename: 'PaginatedContentListWithChild',
			}
		: null,
	space: data?.space || null,
});

export function preloadContent({
	contentId,
	spaceKey,
	isBlog,
	versionOverride = null,
}: PreloadContentParameters): Promise<PreloadContentResult> {
	window.performance.mark('CFP-63.preloadContent.start');
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR);

	// Space overview
	if (spaceKey && !contentId) {
		return query<ContentUnifiedWithSpaceKeyQueryType, ContentUnifiedWithSpaceKeyQueryVariablesType>(
			{
				/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
				query: ContentUnifiedWithSpaceKeyQuery,
				variables: {
					spaceKey,
					versionOverride,
					isSSR,
					useNewContentTopper: true,
				},
				errorPolicy: 'all',
				context: { headers: { 'X-ATL-WARM-CACHE-HINT': 'get' } },
			},
		).then((result: any) => {
			// Record the end mark and timestamp to track *both* duration of preloading
			// and timestamp of the moment when preloading finished
			window.performance.mark('CFP-63.preloadContent.end');
			window.performance.mark('CFP-63.preloadContentEnd');

			const data = result?.data;
			const homepageId = data?.spaceHomepage?.id;
			if (homepageId) {
				// update result with transformed data
				result.data = transformContentData(data);

				writeQuery<ContentUnifiedQueryType, ContentUnifiedQueryVariablesType>({
					/* @ts-expect-error http://go/cfe-use-relay-artifacts */
					query: ContentUnifiedQuery,
					variables: {
						contentId: homepageId,
						spaceKey,
						versionOverride,
						isSSR,
						useNewContentTopper: true,
					},
					data: { ...result.data },
				});
			}

			processContentPreloadPermissionError(result);

			return { result, hasErrors: result?.hasErrors };
		});
	}

	const chooseContentUnifiedQuery = isBlog ? ContentBlogUnifiedQuery : ContentUnifiedQuery;
	const queryVariables: ChooseContentUnifiedQueryVariablesType = {
		contentId,
		spaceKey,
		versionOverride,
		isSSR,
		useNewContentTopper: true,
	};

	if (isSSR) {
		(queryVariables as ContentUnifiedQueryVariablesType).preloadLabels = true;
	}

	return query<ContentUnifiedQueryType, ContentUnifiedQueryVariablesType>({
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		query: chooseContentUnifiedQuery,
		variables: queryVariables,
		errorPolicy: 'all',
		context: { headers: { 'X-ATL-WARM-CACHE-HINT': 'get' } },
	}).then((result: any) => {
		// Record the end mark and timestamp to track *both* duration of preloading
		// and timestamp of the moment when preloading finished
		window.performance.mark('CFP-63.preloadContent.end');
		window.performance.mark('CFP-63.preloadContentEnd');

		processContentPreloadPermissionError(result);

		return { result, hasErrors: result?.hasErrors };
	});
}
